import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, ArrowLeft, Plane, Shield, ListTodo, Users, Award, FileText, Target } from 'lucide-react';
import apiClient from '../api/client';

const LEAVE_TYPES = { vacation: 'Vacation', sick: 'Sick', personal: 'Personal', other: 'Other' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;

const MyReviewCard = ({ review, onUpdate }) => {
  const [selfReview, setSelfReview] = useState(review.self_review || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSelfReview(review.self_review || ''); }, [review.id, review.self_review]);
  const saveDraft = async () => {
    setSaving(true);
    try {
      await apiClient.updateMyReview(review.id, { self_review: selfReview, status: 'draft' });
      onUpdate();
    } catch {}
    setSaving(false);
  };
  const submitSelf = async () => {
    setSaving(true);
    try {
      await apiClient.updateMyReview(review.id, { self_review: selfReview, status: 'self_submitted' });
      onUpdate();
    } catch {}
    setSaving(false);
  };
  return (
    <div className="p-4 border border-gray-200 rounded-xl">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-text">{review.cycle_name}</span>
        <span className={`text-xs px-2 py-0.5 rounded ${review.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : review.status === 'self_submitted' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{review.status}</span>
      </div>
      <textarea value={selfReview} onChange={e => setSelfReview(e.target.value)} rows={4} placeholder="Self-review..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none mb-2" disabled={review.status === 'completed'} />
      {review.status !== 'completed' && (
        <div className="flex gap-2">
          <button onClick={saveDraft} disabled={saving} className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Save draft</button>
          <button onClick={submitSelf} disabled={saving} className="px-3 py-1.5 text-sm font-medium bg-[#37352F] text-white rounded-lg">Submit self-review</button>
        </div>
      )}
    </div>
  );
};

/**
 * My HR — self-service: Profile, My Time Off, My Benefits, My Onboarding.
 * Requires auth. Employee record must be linked to the account.
 */
const HRProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    phone: '', emergency_contact: '', bio: '', profile_photo_url: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  });
  const [saving, setSaving] = useState(false);

  const [timeOff, setTimeOff] = useState(null);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', notes: '' });
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [benefits, setBenefits] = useState([]);
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [myTeam, setMyTeam] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [docForm, setDocForm] = useState({ title: '', url: '', category: '' });
  const [myGoals, setMyGoals] = useState([]);
  const [myDependents, setMyDependents] = useState([]);
  const [dependentForm, setDependentForm] = useState({ name: '', relationship: '', date_of_birth: '' });
  const [showDependentForm, setShowDependentForm] = useState(false);

  const hasToken = !!localStorage.getItem('auth_token');

  useEffect(() => {
    if (!hasToken) { setLoading(false); return; }
    apiClient.getMyProfile()
      .then(p => {
        setProfile(p);
        setForm({
          phone: p.phone || '', emergency_contact: p.emergency_contact || '', bio: p.bio || '', profile_photo_url: p.profile_photo_url || '',
          emergency_contact_name: p.emergency_contact_name || '', emergency_contact_phone: p.emergency_contact_phone || '', emergency_contact_relationship: p.emergency_contact_relationship || '',
        });
      })
      .catch(e => { setError(e.message || 'Could not load profile'); })
      .finally(() => setLoading(false));
  }, [hasToken]);

  useEffect(() => {
    if (!hasToken || !profile) return;
    if (activeTab === 'timeoff') {
      apiClient.getMyTimeOff().then(setTimeOff).catch(() => setTimeOff(null));
    } else if (activeTab === 'benefits') {
      apiClient.getMyBenefits().then(setBenefits).catch(() => setBenefits([]));
    } else if (activeTab === 'onboarding') {
      apiClient.getMyOnboardingTasks().then(setOnboardingTasks).catch(() => setOnboardingTasks([]));
    } else if (activeTab === 'team') {
      apiClient.getMyTeam().then(r => setMyTeam(r.members || [])).catch(() => setMyTeam([]));
    } else if (activeTab === 'reviews') {
      apiClient.getMyReviews().then(setMyReviews).catch(() => setMyReviews([]));
    } else if (activeTab === 'documents') {
      apiClient.getMyDocuments().then(setMyDocuments).catch(() => setMyDocuments([]));
    } else if (activeTab === 'goals') {
      apiClient.getMyGoals().then(setMyGoals).catch(() => setMyGoals([]));
    } else if (activeTab === 'dependents') {
      apiClient.getMyDependents().then(setMyDependents).catch(() => setMyDependents([]));
    }
  }, [hasToken, profile, activeTab]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const updated = await apiClient.updateMyProfile(form);
      setProfile(updated);
      setForm({
        phone: updated.phone || '', emergency_contact: updated.emergency_contact || '', bio: updated.bio || '', profile_photo_url: updated.profile_photo_url || '',
        emergency_contact_name: updated.emergency_contact_name || '', emergency_contact_phone: updated.emergency_contact_phone || '', emergency_contact_relationship: updated.emergency_contact_relationship || '',
      });
    } catch (e) { setError(e.message || 'Could not save'); }
    setSaving(false);
  };

  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) return;
    try {
      await apiClient.createMyLeaveRequest(leaveForm);
      const data = await apiClient.getMyTimeOff();
      setTimeOff(data);
      setShowLeaveForm(false);
      setLeaveForm({ leave_type: 'vacation', start_date: '', end_date: '', notes: '' });
    } catch {}
  };

  const toggleOnboardingTask = async (task) => {
    try {
      await apiClient.updateMyOnboardingTask(task.id, { completed: !task.completed });
      const list = await apiClient.getMyOnboardingTasks();
      setOnboardingTasks(list);
    } catch {}
  };

  if (!hasToken) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12">
        <Link to="/hr" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-text mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to People
        </Link>
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-text mb-2">My HR</h2>
          <p className="text-gray-500 mb-6">Log in to view your profile, time off, benefits, and onboarding.</p>
          <Link to="/login" className="inline-block px-5 py-2.5 bg-[#37352F] text-white rounded-lg text-sm font-medium hover:bg-[#4a4843] transition-colors">
            Log in
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="flex justify-center py-16"><div className="h-8 w-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12">
        <Link to="/hr" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-text mb-6"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-center">
          <p className="text-red-600">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Make sure your account is linked to an employee record.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'timeoff', label: 'My Time Off', icon: Plane },
    { id: 'benefits', label: 'My Benefits', icon: Shield },
    { id: 'onboarding', label: 'My Onboarding', icon: ListTodo },
    { id: 'reviews', label: 'My Reviews', icon: Award },
    { id: 'goals', label: 'My Goals', icon: Target },
    { id: 'dependents', label: 'Dependents', icon: Users },
    { id: 'documents', label: 'My Documents', icon: FileText },
    { id: 'team', label: 'My Team', icon: Users },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <Link to="/hr" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-text mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to People
      </Link>
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${activeTab === id ? 'border-[#37352F] text-text' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-text">My Profile</h2>
            <p className="text-sm text-gray-500">Edit your contact info, bio, and profile photo URL</p>
          </div>
          <form onSubmit={handleSave} className="p-6 space-y-4">
            {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Profile Photo URL</label>
              <input value={form.profile_photo_url} onChange={e => setForm(f => ({ ...f, profile_photo_url: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Display Name</label>
              <p className="text-sm text-text font-medium">{profile?.display_name}</p>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Email</label>
              <p className="text-sm text-text">{profile?.email}</p>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none" placeholder="(555) 123-4567" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Emergency Contact</label>
              <div className="grid grid-cols-1 gap-2 mt-1">
                <input value={form.emergency_contact_name} onChange={e => setForm(f => ({ ...f, emergency_contact_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none" placeholder="Name" />
                <input value={form.emergency_contact_phone} onChange={e => setForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none" placeholder="Phone" />
                <input value={form.emergency_contact_relationship} onChange={e => setForm(f => ({ ...f, emergency_contact_relationship: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none" placeholder="Relationship (e.g. Spouse)" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1 uppercase">Bio</label>
              <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 outline-none resize-none" placeholder="Short bio (optional)" />
            </div>
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={saving} className="px-5 py-2 bg-[#37352F] text-white rounded-lg text-sm font-medium hover:bg-[#4a4843] disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'timeoff' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Time Off</h2>
          {timeOff?.accrual_policy && (
            <p className="text-sm text-gray-500 mb-3">Accrual: {timeOff.accrual_policy.hours_per_month}h/month{timeOff.accrual_policy.annual_cap_hours ? ` (cap ${timeOff.accrual_policy.annual_cap_hours}h/year)` : ''}</p>
          )}
          {(timeOff?.balance || (timeOff?.pto_balances_by_type?.length > 0)) && (
            timeOff.pto_balances_by_type?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {timeOff.pto_balances_by_type.map(b => (
                  <div key={b.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-[11px] text-gray-500 uppercase">{LEAVE_TYPES[b.leave_type] || b.leave_type}</p>
                    <p className="text-lg font-bold text-text">{Number(b.accrued_hours) - Number(b.used_hours)}h left</p>
                    <p className="text-xs text-gray-400">Accrued: {b.accrued_hours}h · Used: {b.used_hours}h</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[11px] text-gray-500 uppercase">Accrued</p>
                  <p className="text-lg font-bold text-text">{timeOff.balance.pto_accrued_hours ?? 0}h</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[11px] text-gray-500 uppercase">Used</p>
                  <p className="text-lg font-bold text-text">{timeOff.balance.pto_used_hours ?? 0}h</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-[11px] text-emerald-600 uppercase">Remaining</p>
                  <p className="text-lg font-bold text-emerald-700">{timeOff.balance.pto_remaining_hours ?? 0}h</p>
                </div>
              </div>
            )
          )}
          <p className="text-[11px] text-gray-500 uppercase mb-2">Leave requests</p>
          <div className="space-y-2 mb-4">
            {(timeOff?.leave_requests || []).map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
                <span>{LEAVE_TYPES[r.leave_type] || r.leave_type} · {fmtDate(r.start_date)} – {fmtDate(r.end_date)}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : r.status === 'denied' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{r.status}</span>
              </div>
            ))}
          </div>
          {!showLeaveForm ? (
            <button onClick={() => setShowLeaveForm(true)} className="text-sm font-medium text-text hover:underline">+ Request leave</button>
          ) : (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <select value={leaveForm.leave_type} onChange={e => setLeaveForm(f => ({ ...f, leave_type: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                {Object.entries(LEAVE_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <textarea value={leaveForm.notes} onChange={e => setLeaveForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={1} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
              <div className="flex gap-2">
                <button onClick={submitLeave} className="px-3 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg">Submit</button>
                <button onClick={() => setShowLeaveForm(false)} className="px-3 py-2 text-sm text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'benefits' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Benefits</h2>
          {benefits.length === 0 ? (
            <p className="text-gray-500 text-sm">No benefit enrollments. Contact HR to enroll.</p>
          ) : (
            <ul className="space-y-2">
              {benefits.map(e => (
                <li key={e.id} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
                  <span className="font-medium">{e.plan_name || e.plan_type}</span>
                  <span className="text-gray-500">{e.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'onboarding' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Onboarding</h2>
          <p className="text-sm text-gray-500 mb-4">{onboardingTasks.filter(t => t.completed).length} of {onboardingTasks.length} completed</p>
          <div className="space-y-2">
            {onboardingTasks.map(t => (
              <div key={t.id} className="flex items-center gap-3 py-2">
                <button onClick={() => toggleOnboardingTask(t)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${t.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-gray-400'}`}>
                  {t.completed && <span className="text-white text-xs">✓</span>}
                </button>
                <span className={`text-sm ${t.completed ? 'text-gray-400 line-through' : 'text-text'}`}>{t.title}</span>
                {t.due_date && <span className="text-xs text-gray-400 ml-auto">{fmtDate(t.due_date)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Reviews</h2>
          <p className="text-sm text-gray-500 mb-4">Submit self-reviews for your performance review cycles.</p>
          {myReviews.length === 0 ? (
            <p className="text-gray-500 text-sm">No review cycles assigned yet.</p>
          ) : (
            <div className="space-y-4">
              {myReviews.map(rev => (
                <MyReviewCard key={rev.id} review={rev} onUpdate={() => apiClient.getMyReviews().then(setMyReviews)} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'goals' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Goals</h2>
          <p className="text-sm text-gray-500 mb-4">Your goals and OKRs.</p>
          {myGoals.length === 0 ? (
            <p className="text-gray-500 text-sm">No goals yet.</p>
          ) : (
            <ul className="space-y-2">
              {myGoals.map(g => (
                <li key={g.id} className="py-2 border-b border-gray-100">
                  <span className="font-medium text-text">{g.title}</span>
                  {g.due_date && <span className="text-gray-400 text-sm ml-2">Due {fmtDate(g.due_date)}</span>}
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded ${g.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : g.status === 'in_progress' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{g.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'dependents' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">Dependents</h2>
          <p className="text-sm text-gray-500 mb-4">Household dependents for benefits or emergency contact.</p>
          {myDependents.length > 0 && (
            <ul className="space-y-2 mb-4">
              {myDependents.map(d => (
                <li key={d.id} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
                  <span className="font-medium text-text">{d.name}</span>
                  <span className="text-gray-500">{d.relationship || ''} {d.date_of_birth ? fmtDate(d.date_of_birth) : ''}</span>
                  <button onClick={async () => { await apiClient.deleteMyDependent(d.id); apiClient.getMyDependents().then(setMyDependents); }} className="text-xs text-red-600 hover:underline">Remove</button>
                </li>
              ))}
            </ul>
          )}
          {!showDependentForm ? (
            <button onClick={() => setShowDependentForm(true)} className="text-sm font-medium text-text hover:underline">+ Add dependent</button>
          ) : (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <input value={dependentForm.name} onChange={e => setDependentForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <input value={dependentForm.relationship} onChange={e => setDependentForm(f => ({ ...f, relationship: e.target.value }))} placeholder="Relationship" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <input type="date" value={dependentForm.date_of_birth} onChange={e => setDependentForm(f => ({ ...f, date_of_birth: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <div className="flex gap-2">
                <button onClick={async () => { if (!dependentForm.name?.trim()) return; await apiClient.createMyDependent({ name: dependentForm.name.trim(), relationship: dependentForm.relationship || undefined, date_of_birth: dependentForm.date_of_birth || undefined }); setDependentForm({ name: '', relationship: '', date_of_birth: '' }); setShowDependentForm(false); apiClient.getMyDependents().then(setMyDependents); }} className="px-3 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg">Add</button>
                <button onClick={() => setShowDependentForm(false)} className="px-3 py-2 text-sm text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Documents</h2>
          <p className="text-sm text-gray-500 mb-4">Links to handbooks, forms, or other documents.</p>
          <ul className="space-y-2 mb-4">
            {myDocuments.map(d => (
              <li key={d.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-text font-medium hover:underline truncate max-w-[200px]">{d.title}</a>
                <button onClick={async () => { await apiClient.deleteMyDocument(d.id); apiClient.getMyDocuments().then(setMyDocuments); }} className="text-xs text-red-600 hover:underline">Remove</button>
              </li>
            ))}
          </ul>
          <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
            <input value={docForm.title} onChange={e => setDocForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <input value={docForm.url} onChange={e => setDocForm(f => ({ ...f, url: e.target.value }))} placeholder="URL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <input value={docForm.category} onChange={e => setDocForm(f => ({ ...f, category: e.target.value }))} placeholder="Category (optional)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <button onClick={async () => { if (!docForm.title || !docForm.url) return; await apiClient.createMyDocument({ title: docForm.title, url: docForm.url, category: docForm.category || undefined }); setDocForm({ title: '', url: '', category: '' }); apiClient.getMyDocuments().then(setMyDocuments); }} className="px-3 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg">Add link</button>
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6">
          <h2 className="text-lg font-bold text-text mb-4">My Team</h2>
          <p className="text-sm text-gray-500 mb-4">Direct reports (if you are a manager)</p>
          {myTeam.length === 0 ? (
            <p className="text-gray-500 text-sm">You have no direct reports, or this view is for managers.</p>
          ) : (
            <ul className="space-y-2">
              {myTeam.map(m => (
                <li key={m.id} className="flex items-center gap-3 py-2 border-b border-gray-100">
                  {m.profile_photo_url ? (
                    <img src={m.profile_photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">{(m.display_name || '?').slice(0, 2).toUpperCase()}</div>
                  )}
                  <span className="font-medium text-text">{m.display_name}</span>
                  <span className="text-gray-400 text-sm">{m.position_title || m.department_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HRProfile;
